import { Button } from "~/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel } from "./ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { animated, useSpring } from "react-spring";

const formSchema = z.object({
	email: z.string().email(),
	password: z.string().min(1, "Password is required")
});

type props = {
	submit: (defaultValues: FormData) => void;
	isLoading: boolean;
	defaultEmail?: string;
};
export function LoginForm({ submit, isLoading, defaultEmail = "" }: props) {
	const initAnimation = useSpring({
		from: {
			opacity: "0%",
			y: "50%"
		},
		to: {
			opacity: "100%",
			y: "0%"
		},
		config: {
			tension: 150,
			trail: 5000
		}
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: defaultEmail,
			password: ""
		},
		reValidateMode: "onBlur"
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		const form_data = new FormData();
		form_data.append("email", values.email);
		form_data.append("password", values.password);
		submit(form_data);
	}

	return (
		<animated.div style={initAnimation}>
			<Card className="w-full max-w-sm">
				<CardHeader>
					<CardTitle className="text-2xl">Login</CardTitle>
					<CardDescription>
						Enter your email below to login to your account.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<Form {...form}>
						<form
							method="POST"
							onSubmit={form.handleSubmit(onSubmit)}
							className="grid gap-4"
						>
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem className="grid gap-2">
										<FormLabel htmlFor="email">Email</FormLabel>
										<FormControl>
											<Input
												{...field}
												id="email"
												type="email"
												placeholder="me@example.com"
												required
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem className="grid gap-2">
										<FormLabel htmlFor="password">Password</FormLabel>
										<FormControl>
											<Input
												{...field}
												id="password"
												type="password"
												required
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<Button
								disabled={isLoading}
								className="w-full"
								type="submit"
							>
								{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								Sign in
							</Button>
						</form>
					</Form>
				</CardContent>
			</Card>
		</animated.div>
	);
}
